import { matchPath } from 'react-router-dom';
import SelectLabTab from './Components/Tabs/SelectLabTab';
import NewLabTab from './Components/Tabs/NewLabTab';
import LabNavigationTab from './Components/Tabs/LabNavigationTab';
import ManageUsersTab from './Components/Tabs/ManageUsersTab';
import ManageMachinesTab from './Components/Tabs/ManageMachinesTab';
import NewMachineTab from './Components/Tabs/NewMachineTab';
import MachineDetailsTab from './Components/Tabs/MachineDetailsTab';
import MachineTypesWarningTab from './Components/Tabs/MachineTypesWarningTab';
import ManageMachineTypesTab from './Components/Tabs/ManageMachineTypesTab';
import ToolCategoriesTab from './Components/Tabs/ToolCategoriesTab';
import BadgeMachineTypeWarningTab from './Components/Tabs/BadgeMachineTypeWarningTab';
import EditBadgeMachineTypeTab from './Components/Tabs/EditBadgeMachineTypeTab';
import OtherSettingsTab from './Components/Tabs/OtherSettingsTab';
import DeleteLabTab from './Components/Tabs/DeleteLabTab';
import CustomReasonsTab from './Components/Tabs/CustomReasonsTab';
import AdminToolsTab from './Components/Tabs/AdminToolsTab';
import AdminUserSearchTab from './Components/Tabs/AdminUserSearchTab';
import AdminUserDetailsTab from './Components/Tabs/AdminUserDetailsTab';
import AuditLogTab from './Components/Tabs/AuditLogTab';
import ActiveSessionsTab from './Components/Tabs/ActiveSessionsTab';
import LabSwipeLogTab from './Components/Tabs/LabSwipeLogTab';
import LabStatisticsTab from './Components/Tabs/LabStatisticsTab';
import FailedSwipesTab from './Components/Tabs/FailedSwipesTab';
import DeveloperToolsTab from './Components/Tabs/DeveloperToolsTab';
import EmailDebugTab from './Components/Tabs/EmailDebugTab';

// Stable public paths deliberately do not depend on visible titles or component names.
// Warning screens remain explicit destinations; their existing two-step back actions
// still return from global resource management to the selected space.
export const adminRoutes = [
  { id: 'home', path: '/admin', component: SelectLabTab },
  { id: 'newSpace', path: '/admin/new-space', parent: 'home', component: NewLabTab, superAdmin: true },
  { id: 'space', path: '/admin/:space', parent: 'home', component: LabNavigationTab },
  { id: 'spaceAdmins', path: '/admin/:space/space-admins', parent: 'space', component: ManageUsersTab },
  { id: 'instances', path: '/admin/:space/tool-instances', parent: 'space', component: ManageMachinesTab },
  { id: 'newInstance', path: '/admin/:space/tool-instances/new', parent: 'instances', component: NewMachineTab },
  { id: 'instance', path: '/admin/:space/tool-instances/:machineId(\\d+)', parent: 'instances', component: MachineDetailsTab },
  { id: 'toolsWarning', path: '/admin/:space/tools/review', parent: 'space', component: MachineTypesWarningTab },
  { id: 'tools', path: '/admin/:space/tools', parent: 'toolsWarning', component: ManageMachineTypesTab },
  { id: 'categories', path: '/admin/:space/tool-categories', parent: 'space', component: ToolCategoriesTab },
  { id: 'badgesWarning', path: '/admin/:space/badge-requirements/review', parent: 'space', component: BadgeMachineTypeWarningTab, superAdmin: true },
  { id: 'badges', path: '/admin/:space/badge-requirements', parent: 'badgesWarning', component: EditBadgeMachineTypeTab, superAdmin: true },
  { id: 'settings', path: '/admin/:space/settings', parent: 'space', component: OtherSettingsTab },
  { id: 'deleteSpace', path: '/admin/:space/settings/delete', parent: 'settings', component: DeleteLabTab },
  { id: 'purposes', path: '/admin/:space/check-in-purposes', parent: 'space', component: CustomReasonsTab },
  { id: 'adminTools', path: '/admin/:space/admin-tools', parent: 'space', component: AdminToolsTab },
  { id: 'search', path: '/admin/:space/admin-tools/users', parent: 'adminTools', component: AdminUserSearchTab },
  { id: 'user', path: '/admin/:space/admin-tools/users/:targetUid(\\d+)', parent: 'search', component: AdminUserDetailsTab },
  { id: 'audit', path: '/admin/:space/admin-tools/audit-log', parent: 'adminTools', component: AuditLogTab },
  { id: 'sessions', path: '/admin/:space/admin-tools/active-sessions', parent: 'adminTools', component: ActiveSessionsTab },
  { id: 'log', path: '/admin/:space/admin-tools/check-in-log', parent: 'adminTools', component: LabSwipeLogTab },
  { id: 'statistics', path: '/admin/:space/admin-tools/statistics', parent: 'adminTools', component: LabStatisticsTab },
  { id: 'failed', path: '/admin/:space/admin-tools/failed-check-ins', parent: 'adminTools', component: FailedSwipesTab },
  { id: 'developerTools', path: '/admin/:space/admin-tools/developer-tools', parent: 'adminTools', component: DeveloperToolsTab, superAdmin: true },
  { id: 'email', path: '/admin/:space/admin-tools/developer-tools/email', parent: 'developerTools', component: EmailDebugTab, superAdmin: true },
];

export function adminPath(route, params = {}) {
  return route.path.replace(/:([A-Za-z]+)(?:\([^)]*\))?/g, (_, key) => encodeURIComponent(params[key] ?? ''));
}

export function resolveAdminRoute(pathname) {
  for (const route of adminRoutes) {
    const match = matchPath(pathname, { path: route.path, exact: true });
    if (match) {
      try {
        const params = Object.fromEntries(Object.entries(match.params).map(([key, value]) => [key, decodeURIComponent(value)]));
        if (params.space) params.space = params.space.toLowerCase();
        return { route, params, path: adminPath(route, params) };
      } catch { return null; }
    }
  }
  return null;
}

export function canonicalAdminTrail(route, params) {
  if (!route?.parent) return [];
  const parent = adminRoutes.find(item => item.id === route.parent);
  if (!parent) return ['/admin'];
  return [...canonicalAdminTrail(parent, params), adminPath(parent, params)];
}

export function destinationForTab(element, context = {}) {
  // Explicit IDs survive React Fast Refresh replacing component functions.
  const route = element.props?.adminRoute
    ? adminRoutes.find(item => item.id === element.props.adminRoute)
    : adminRoutes.find(item => item.component === element.type);
  if (!route) throw new Error('This admin page does not have a registered route.');
  const props = element.props || {};
  const params = {
    space: String(props.labId || context.space || '').toLowerCase(),
    machineId: props.machineId,
    targetUid: props.targetUser?.UID,
  };
  return adminPath(route, params);
}
